// Audit note: client.create audit row is written server-side by the
// `create-client-user` edge function (FOUND-05 / Plan 09-04). This component
// does not emit audit rows directly — it only inserts the clients row and
// invokes the edge function which in turn writes the audit row.
import { useState, useRef } from "react";
import { Copy, Check, Mail, CreditCard, Loader2, ExternalLink, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  generateClientCredentials,
  copyToClipboard,
  copyAllCredentials,
  type GeneratedCredentials
} from "@/utils/clientUtils";
import { Client } from "@/types/client";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { addClientSchema } from "@/lib/validation";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";

interface AddClientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddClient: (client: Omit<Client, "id">) => void;
}

type ModalStep = 'form' | 'success';

export function AddClientModal({ open, onOpenChange, onAddClient }: AddClientModalProps) {
  const [step, setStep] = useState<ModalStep>('form');
  const [createdCredentials, setCreatedCredentials] = useState<GeneratedCredentials | null>(null);
  const [createdClient, setCreatedClient] = useState<string>("");
  const [createdClientId, setCreatedClientId] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);

  // Phase 11 Plan 05: release.billing_v2 gates subscription-checkout creation (D-09, PAY-10)
  const billingV2 = useFeatureFlag('release.billing_v2');

  // Fanbasis checkout state
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [subscriptionPaymentLink, setSubscriptionPaymentLink] = useState<string | null>(null);
  const [checkoutFailed, setCheckoutFailed] = useState(false);
  const [retryingCheckout, setRetryingCheckout] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const [formData, setFormData] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    package: "standard" as "standard" | "elite" | "test",
    demos: true,
  });

  const setupFeeCents =
    formData.package === 'elite' ? 515000 :
    formData.package === 'test' ? 100 :
    315000;
  const setupFeeFormatted =
    formData.package === 'elite' ? '$5,150' :
    formData.package === 'test' ? '$1' :
    '$3,150';

  const resetForm = () => {
    setFormData({
      companyName: "",
      contactName: "",
      email: "",
      phone: "",
      package: "standard",
      demos: true,
    });
    setStep('form');
    setCreatedCredentials(null);
    setCreatedClientId(null);
    setSubmitting(false);
    submitLock.current = false;
    setPaymentLink(null);
    setSubscriptionPaymentLink(null);
    setCheckoutFailed(false);
    setRetryingCheckout(false);
    setShowCloseConfirm(false);
  };

  const handleClose = () => {
    if (checkoutFailed) {
      setShowCloseConfirm(true);
      return;
    }
    onOpenChange(false);
    setTimeout(resetForm, 300);
  };

  const handleConfirmDiscard = () => {
    setShowCloseConfirm(false);
    onOpenChange(false);
    setTimeout(resetForm, 300);
  };

  // M-07 (12-05a): invoke the delete-client-orphan edge fn. Best-effort,
  // dependency-ordered cleanup (payment_methods → clients → auth.users);
  // auth.users + public.clients can't share a single Postgres txn, so this
  // is NOT an all-or-nothing rollback. If any step returns non-'ok', surface
  // a "rollback may be partial — check Command Center" toast.
  const invokeOrphanCleanup = async (
    client_id: string | null | undefined,
    user_id: string | null | undefined,
  ) => {
    try {
      const { data, error } = await supabase.functions.invoke(
        'delete-client-orphan',
        { body: { client_id: client_id ?? undefined, user_id: user_id ?? undefined } },
      );
      if (error) {
        toast.error('Orphan cleanup failed — check Command Center');
        return;
      }
      const steps = (data as { steps?: Array<{ status: string }> } | null)?.steps ?? [];
      const hasError = steps.some((s) => s.status === 'error');
      if (hasError) {
        toast.error('Rollback may be partial — check Command Center');
      }
    } catch {
      toast.error('Orphan cleanup failed — check Command Center');
    }
  };

  const handleSubmit = async () => {
    // Prevent double-click: ref is synchronous, blocks immediately
    if (submitLock.current) return;
    submitLock.current = true;

    // Validate security-critical fields with Zod (SEC-08, D-09)
    const clientValidation = addClientSchema.safeParse({
      companyName: formData.companyName,
      contactName: formData.contactName,
      email: formData.email,
      phone: formData.phone || undefined,
      package: formData.package,
    });

    if (!clientValidation.success) {
      const firstError = clientValidation.error.errors[0]?.message ?? 'Invalid input';
      toast.error(firstError);
      submitLock.current = false;
      return;
    }

    setSubmitting(true);

    // Generate VH username: VH{COMPANYNAME}{2DIGITS}
    const cleanCompanyName = formData.companyName.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 15);
    const randomDigits = String(Math.floor(Math.random() * 90) + 10);
    const vhUsername = `VH${cleanCompanyName}${randomDigits}`;
    // Generate random password (not same as username)
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    const initialPassword = 'VH-' + Array.from(crypto.getRandomValues(new Uint8Array(8)))
      .map(b => chars[b % chars.length]).join('');

    const credentials = generateClientCredentials(formData.companyName);
    credentials.username = vhUsername;
    credentials.password = initialPassword;

    try {
      // Create client in Supabase
      const { data: newClientData, error: clientError } = await supabase
        .from('clients')
        .insert({
          company_name: formData.companyName,
          contact_name: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          package: formData.package,
          status: 'active',
          username: vhUsername,
          onboarding_complete: false,
          credentials: {
            username: vhUsername,
            initialPassword: initialPassword,
            clientCode: credentials.clientCode,
            last_updated: new Date().toISOString(),
          },
          services_enabled: { demos: formData.demos },
          metadata: {
            integrations: { ghl: false, metaAds: false, googleAds: false }
          },
          setup_fee_cents: setupFeeCents
        })
        .select()
        .single();

      if (clientError) throw clientError;

      // Create Supabase Auth user for client login
      const { data: authResult, error: authFnError } = await supabase.functions.invoke(
        'create-client-user',
        {
          body: {
            email: formData.email,
            password: initialPassword,
            client_id: newClientData.id,
            company_name: formData.companyName,
          },
        }
      );

      if (authFnError) {
        // M-07 (12-05a): best-effort, dependency-ordered cleanup via edge fn.
        // auth.users + public.clients can't share a Postgres txn; see
        // supabase/functions/delete-client-orphan/cleanup.ts for the contract.
        await invokeOrphanCleanup(newClientData.id, newClientData.user_id);
        let detail = 'Failed to create login credentials';
        try {
          const ctx = (authFnError as any).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            detail = body?.error || body?.hint || body?.detail || detail;
          }
        } catch {}
        throw new Error(detail);
      }

      const authBody = typeof authResult === 'string' ? JSON.parse(authResult) : authResult;
      if (authBody?.error) {
        // M-07 (12-05a): same best-effort cleanup path.
        await invokeOrphanCleanup(newClientData.id, newClientData.user_id);
        throw new Error(authBody.error);
      }

      // Store for later steps
      setCreatedClientId(newClientData.id);
      setCreatedCredentials(credentials);
      setCreatedClient(formData.companyName);

      // Create client object for local state (legacy)
      const newClient: Omit<Client, "id"> = {
        name: formData.companyName,
        contactName: formData.contactName,
        email: formData.email,
        package: formData.package,
        services: { demos: formData.demos },
        integrations: { ghl: false, metaAds: false, googleAds: false },
        status: "active",
        joinedDate: new Date().toISOString().split("T")[0],
        lastContact: new Date().toISOString().split("T")[0],
        credentials: {
          username: vhUsername,
          password: initialPassword,
          clientCode: credentials.clientCode,
        },
      };

      onAddClient(newClient);

      // Generate Fanbasis checkout session via edge function
      try {
        const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke(
          'create-checkout-session',
          { body: { client_id: newClientData.id } }
        );

        if (checkoutError || !checkoutData?.payment_link) {
          let detail = 'Checkout session creation failed';
          try {
            const ctx = (checkoutError as any)?.context;
            if (ctx && typeof ctx.json === 'function') {
              const body = await ctx.json();
              detail = body?.error || body?.detail || detail;
            } else if (checkoutError instanceof Error) {
              detail = checkoutError.message;
            }
          } catch {}
          console.error('create-checkout-session failed:', detail);
          setCheckoutFailed(true);
          setStep('success');
          toast.error(`Client created, but payment link failed: ${detail}. Retry from the success screen.`);
          return;
        }

        setPaymentLink(checkoutData.payment_link);
        setCheckoutFailed(false);

        // Phase 11 Plan 05 (D-09/PAY-10): when release.billing_v2 is on,
        // additionally create a weekly subscription checkout alongside the
        // setup-fee link. Both links coexist in the success step.
        if (billingV2) {
          try {
            const { data: subData, error: subErr } = await supabase.functions.invoke(
              'fanbasis-create-subscription-checkout',
              { body: { client_id: newClientData.id } },
            );
            if (subErr || !subData?.payment_link) {
              toast.error('Subscription checkout creation failed — you can retry from Command Center');
            } else {
              setSubscriptionPaymentLink(subData.payment_link);
            }
          } catch (subInvokeErr) {
            console.error('fanbasis-create-subscription-checkout failed:', subInvokeErr);
            toast.error('Subscription checkout creation failed — you can retry from Command Center');
          }
        }

        window.open(checkoutData.payment_link, '_blank');
        setStep('success');
        toast.success(`Client ${formData.companyName} created! Payment page opened in a new tab.`);
      } catch (invokeErr: any) {
        console.error('create-checkout-session invoke threw:', invokeErr);
        setCheckoutFailed(true);
        setStep('success');
        toast.error(`Client created, but payment link failed: ${invokeErr?.message || 'Unknown error'}. Retry from the success screen.`);
      }
    } catch (error: any) {
      console.error('Error creating client:', error);
      toast.error(error.message || 'Failed to create client');
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  const handleRetryCheckout = async () => {
    if (!createdClientId) return;
    setRetryingCheckout(true);
    try {
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke(
        'create-checkout-session',
        { body: { client_id: createdClientId } }
      );

      if (checkoutError || !checkoutData?.payment_link) {
        let detail = 'Retry failed';
        try {
          const ctx = (checkoutError as any)?.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            detail = body?.error || body?.detail || detail;
          } else if (checkoutError instanceof Error) {
            detail = checkoutError.message;
          }
        } catch {}
        toast.error(detail);
        return;
      }

      setPaymentLink(checkoutData.payment_link);
      setCheckoutFailed(false);
      window.open(checkoutData.payment_link, '_blank');
      toast.success('Payment link generated! Page opened in a new tab.');
    } catch (err: any) {
      toast.error(err?.message || 'Retry failed');
    } finally {
      setRetryingCheckout(false);
    }
  };

  const handleOpenPaymentPage = () => {
    if (paymentLink) window.open(paymentLink, '_blank');
  };

  const handleCopy = async (field: string, value: string) => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopiedField(field);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleCopyAll = async () => {
    if (createdCredentials) {
      const success = await copyToClipboard(copyAllCredentials(createdCredentials));
      if (success) {
        toast.success("All credentials copied!");
      }
    }
  };

  const isFormValid = formData.companyName && formData.contactName && formData.email && formData.demos;

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-surface border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        {step === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle>Add New Client</DialogTitle>
              <DialogDescription>
                Enter client details. You'll add their payment method in the next step.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Company Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Company Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name *</Label>
                    <Input
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      placeholder="Acme Corp"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Name *</Label>
                    <Input
                      value={formData.contactName}
                      onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                      placeholder="John Smith"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@acme.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Package & Services */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Package & Services
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, package: "standard" })}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      formData.package === "standard"
                        ? "border-secondary bg-secondary/10"
                        : "border-border hover:border-muted-foreground"
                    }`}
                  >
                    <p className="font-medium">Standard</p>
                    <p className="text-sm text-muted-foreground">$3,150 setup + $150/demo</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, package: "elite" })}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      formData.package === "elite"
                        ? "border-amber-500 bg-amber-500/10"
                        : "border-border hover:border-muted-foreground"
                    }`}
                  >
                    <p className="font-medium">Elite</p>
                    <p className="text-sm text-muted-foreground">$5,150 setup + $200/demo</p>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, package: "test" })}
                  className={`w-full p-4 rounded-lg border text-left transition-all ${
                    formData.package === "test"
                      ? "border-emerald-500 bg-emerald-500/10"
                      : "border-border hover:border-muted-foreground"
                  }`}
                >
                  <p className="font-medium">Test ($1 charge for testing purposes)</p>
                  <p className="text-sm text-muted-foreground">$1 setup + $1/demo — end-to-end prod validation, real card</p>
                </button>
                <div className="space-y-2">
                  <Label>Services Included</Label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.demos}
                        onCheckedChange={(checked) => setFormData({ ...formData, demos: !!checked })}
                      />
                      <span>Demos (Qualified demo delivery)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={!isFormValid || submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create & Add Payment'
                )}
              </Button>
            </DialogFooter>
          </>
        ) : checkoutFailed ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-500">
                <AlertTriangle className="w-5 h-5" />
                Payment Link Failed
              </DialogTitle>
              <DialogDescription>
                Client record for <strong>{createdClient}</strong> was created, but the Fanbasis payment link could not be generated.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <Alert className="bg-red-500/10 border-red-500/40">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <AlertDescription className="text-red-200 font-medium">
                  This client cannot pay yet. Credentials are hidden until you successfully generate a payment link.
                  Retry below — if Fanbasis is having a hiccup, waiting a moment and retrying usually works.
                </AlertDescription>
              </Alert>

              <Button
                onClick={handleRetryCheckout}
                disabled={retryingCheckout}
                className="w-full"
                variant="default"
              >
                {retryingCheckout ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry Checkout
                  </>
                )}
              </Button>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCloseConfirm(true)}>
                Close & Discard
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                Client Created Successfully!
              </DialogTitle>
              <DialogDescription>
                Client account for <strong>{createdClient}</strong> has been created.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <Alert className="bg-blue-500/10 border-blue-500/30">
                <AlertDescription>
                  Share these credentials with your client. Copy the username and password below — the password is randomly generated and must be copied exactly.
                </AlertDescription>
              </Alert>

              {paymentLink && (
                <Button
                  onClick={handleOpenPaymentPage}
                  variant="outline"
                  className="w-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Payment Page (Setup Fee)
                </Button>
              )}

              {subscriptionPaymentLink && (
                <Button
                  onClick={() => window.open(subscriptionPaymentLink, '_blank')}
                  variant="outline"
                  className="w-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Weekly Subscription Checkout
                </Button>
              )}

              {createdCredentials && (
                <div className="space-y-3">
                  <CredentialRow
                    label="Username"
                    value={createdCredentials.username}
                    copied={copiedField === 'username'}
                    onCopy={() => handleCopy('username', createdCredentials.username)}
                  />
                  <CredentialRow
                    label="Password"
                    value={createdCredentials.password}
                    copied={copiedField === 'password'}
                    onCopy={() => handleCopy('password', createdCredentials.password)}
                  />
                  <CredentialRow
                    label="Client Code"
                    value={createdCredentials.clientCode}
                    copied={copiedField === 'clientCode'}
                    onCopy={() => handleCopy('clientCode', createdCredentials.clientCode)}
                  />
                  <CredentialRow
                    label="Login URL"
                    value={createdCredentials.loginUrl}
                    copied={copiedField === 'loginUrl'}
                    onCopy={() => handleCopy('loginUrl', createdCredentials.loginUrl)}
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={handleCopyAll}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All Credentials
                </Button>
                <Button variant="outline" className="flex-1" onClick={async () => {
                  if (!createdCredentials) return;
                  try {
                    const { error } = await supabase.functions.invoke('send-client-credentials', {
                      body: {
                        email: formData.email,
                        companyName: createdClient,
                        contactName: formData.contactName,
                        username: createdCredentials.username,
                        password: createdCredentials.password,
                        clientCode: createdCredentials.clientCode,
                        loginUrl: createdCredentials.loginUrl,
                      },
                    });
                    if (error) throw error;
                    toast.success('Credentials emailed to client!');
                  } catch {
                    toast.error('Failed to send email');
                  }
                }}>
                  <Mail className="w-4 h-4 mr-2" />
                  Email to Client
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>

    <Dialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
      <DialogContent className="bg-surface border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-500">
            <AlertTriangle className="w-5 h-5" />
            Discard Broken Client?
          </DialogTitle>
          <DialogDescription>
            This client has no payment link and <strong>cannot pay</strong>. Closing now leaves the record in a broken state — you'll need to either retry checkout from the Command Center or delete the client entirely.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => setShowCloseConfirm(false)}>
            Keep Editing
          </Button>
          <Button variant="destructive" onClick={handleConfirmDiscard}>
            Discard & Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function CredentialRow({
  label,
  value,
  copied,
  onCopy
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-mono font-medium">{value}</p>
      </div>
      <Button variant="ghost" size="sm" onClick={onCopy}>
        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
      </Button>
    </div>
  );
}
